import { useEffect, useState } from "react";
import Slider from "react-slick";
import clsx from "clsx";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { useBreakpointContext } from "@/contexts/BreakpointContext";
import { BREAKPOINTS } from "@/constants/breakpoints";
import ProductCard from "@/components/features/products/ProductCard";
import ProductCardSkeleton from "@/components/features/products/ProductCardSkeleton";
import type { Product } from "@/types/products";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./ProductsSlider.css";

interface ProductsSliderProps {
  products: Product[];
  isLoading: boolean;
}

const getSlidesToShow = (isMobile: boolean) => (isMobile ? 2 : 4);

const ProductsSlider = ({ products, isLoading = true }: ProductsSliderProps) => {
  const { isMobile } = useBreakpointContext();
  const [slidesToShow, setSlidesToShow] = useState(getSlidesToShow(isMobile));
  const [key, setKey] = useState(0); // 用於強制重新初始化 Slider

  useEffect(() => {
    setSlidesToShow(getSlidesToShow(isMobile));
  }, [isMobile]);

  useEffect(() => {
    // 當 products 改變時，更新 key 以強制重新渲染 Slider
    setKey((prevKey) => prevKey + 1);
  }, [products]);

  const settings = {
    dots: false,
    infinite: false,
    arrows: true,
    autoplay: false,
    slidesToShow: slidesToShow,
    slidesToScroll: slidesToShow,
    lazyLoad: "anticipated" as const,
    responsive: [
      {
        breakpoint: BREAKPOINTS.md,
        settings: {
          slidesToShow: slidesToShow,
          slidesToScroll: slidesToShow,
          swipe: true,
        },
      },
    ],
  };

  if (isLoading) {
    const skeletonCount = slidesToShow;
    return (
      <div id="products-slider" className="">
        <div className="grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-4">
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <ProductCardSkeleton key={index} />
          ))}
        </div>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="flex min-h-48 items-center justify-center text-base-content lg:min-h-64 xl:min-h-96">
        暫時沒有符合的商品
      </div>
    );
  }

  return (
    <div id="products-slider" className="-mx-2">
      <Slider
        {...settings}
        key={key} // 使用 key 來強制重新初始化 Slider
        nextArrow={<NextArrow slidesToShow={slidesToShow} />}
        prevArrow={<PrevArrow />}
      >
        {products.map((product) => (
          <div key={product.id} className="px-2">
            <ProductCard product={product} />
          </div>
        ))}
      </Slider>
    </div>
  );
};

const NextArrow = (props: {
  className?: string;
  onClick?: () => void;
  currentSlide?: number;
  slideCount?: number;
  slidesToShow?: number;
}) => {
  const { className, onClick, currentSlide = 0, slideCount = 0, slidesToShow = 0 } = props;
  const isDisabled = currentSlide >= slideCount - slidesToShow;

  return (
    <div
      className={clsx(className, isDisabled ? "cursor-auto opacity-30" : "")}
      onClick={!isDisabled ? onClick : undefined}
    >
      <ChevronRightIcon className="box-content size-6 text-black" />
    </div>
  );
};

const PrevArrow = (props: { className?: string; onClick?: () => void; currentSlide?: number }) => {
  const { className, onClick, currentSlide } = props;
  const isDisabled = currentSlide === 0;

  return (
    <div
      className={clsx(className, isDisabled ? "cursor-auto opacity-30" : "")}
      onClick={!isDisabled ? onClick : undefined}
    >
      <ChevronLeftIcon className="box-content size-6 text-black" />
    </div>
  );
};

export default ProductsSlider;
